import type { ReactNode } from 'react'

import { cn, Label, RadioGroupItem } from '@massilia/ui'

interface PaymentMethodOptionProps {
  id: string
  description: ReactNode
  disabled?: boolean
  /** Message d'indisponibilité affiché sous le libellé quand l'option est désactivée. */
  disabledHint?: ReactNode
  /** Mention courte accolée au libellé quand l'option est désactivée. */
  disabledMessage?: string
  isFirst?: boolean
  isLast?: boolean
  isSelected: boolean
  label: string
  nextIsSelected?: boolean
  prevIsSelected?: boolean
  value: string
}

/**
 * Ligne sélectionnable d'un choix de moyen de paiement : le détail se déplie sous le
 * libellé une fois l'option cochée.
 *
 * Partagée entre le tunnel de réservation et la page publique /payer-acompte pour que les
 * deux parcours proposent exactement la même mécanique visuelle. À utiliser dans un
 * `RadioGroup`.
 */
export function PaymentMethodOption({
  id,
  description,
  disabled = false,
  disabledHint,
  disabledMessage,
  isFirst = false,
  isLast = false,
  isSelected,
  label,
  nextIsSelected = false,
  prevIsSelected = false,
  value,
}: PaymentMethodOptionProps) {
  return (
    <div
      className={cn(
        'border-gray-light-300 border-l border-r transition-all',
        isFirst && 'rounded-t-2xl border-t',
        isLast && 'rounded-b-2xl border-b',
        isSelected && 'border-brand-600 bg-brand-50 border-b border-t',
        !isSelected && prevIsSelected && 'border-t',
        !isSelected && nextIsSelected && 'border-b',
        !isFirst && !isSelected && !prevIsSelected && 'border-t',
        !isLast && !isSelected && !nextIsSelected && 'border-b',
        disabled && 'opacity-50'
      )}
    >
      <div
        className={cn(
          'flex items-start gap-2 p-4',
          disabled ? 'cursor-not-allowed' : 'cursor-pointer'
        )}
      >
        <RadioGroupItem className="mt-0.5" disabled={disabled} id={id} value={value} />
        <Label
          className={cn(
            'flex-1 text-sm font-semibold',
            disabled ? 'cursor-not-allowed' : 'cursor-pointer'
          )}
          htmlFor={id}
        >
          {label}
          {disabled && disabledMessage && (
            <span className="text-gray-light-500 ml-2 text-xs font-normal">{disabledMessage}</span>
          )}
        </Label>
      </div>
      {/*
        `inert` en plus de `pointer-events-none` : ce dernier n'écarte que la souris. Sans
        lui, les champs d'un moyen de paiement non retenu restent tabulables et lisibles par
        les lecteurs d'écran alors qu'ils sont invisibles — et sur la page de règlement, où
        chaque option porte un formulaire complet, un Entrée depuis un champ masqué soumet le
        mauvais. `inert` les sort de l'ordre de tabulation ET de l'arbre d'accessibilité.
      */}
      <div
        className={cn(
          'overflow-hidden transition-all duration-300 ease-in-out',
          isSelected ? 'max-h-250 opacity-100' : 'pointer-events-none max-h-0 opacity-0'
        )}
        inert={!isSelected}
      >
        <div className="px-4 pb-4">{description}</div>
      </div>
      {/* Motif d'indisponibilité, quand l'option est désactivée et donc non dépliable */}
      {disabled && !isSelected && disabledHint && <div className="px-4 pb-3">{disabledHint}</div>}
    </div>
  )
}
